import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PopupBlockedError, PopupClosedError } from '@xinosolutions/auth-sdk';
import { getXsAuthErrorMessage, useAuth, useAuthActions, clearProductCsrfToken } from '@xinosolutions/xs-auth-react';
import { clearCsrfToken } from '../bl_libs/invokeApi';

export function useLogout(options = {}) {
  const {
    onRevokedAll,
    onRestored,
    onPartial,
    onError,
    homePath = '/login',
    clearLocalSession,
  } = options;

  const navigate = useNavigate();
  const { userId } = useAuth();
  const { signOut: signOutAction } = useAuthActions();
  const [pending, setPending] = useState(false);

  const clearLearnixSession = useCallback(async () => {
    clearProductCsrfToken();
    clearCsrfToken();
    clearLocalSession?.();
  }, [clearLocalSession]);

  const signOut = useCallback(async () => {
    if (pending) return;

    setPending(true);
    try {
      await signOutAction({
        activeUserId: userId ?? undefined,
        onRevokedAll: () => {
          void clearLearnixSession();
          onRevokedAll?.();
          navigate(homePath);
        },
        onRestored: () => {
          void clearLearnixSession();
          onRestored?.();
        },
        onPartial: () => {
          onPartial?.();
        },
      });
    } catch (err) {
      if (err instanceof PopupClosedError) {
        return;
      }
      const message =
        err instanceof PopupBlockedError
          ? 'Please allow popups for this site and try again.'
          : getXsAuthErrorMessage(err, 'Could not complete sign out.');
      onError?.(message);
    } finally {
      setPending(false);
    }
  }, [
    clearLearnixSession,
    homePath,
    navigate,
    onError,
    onPartial,
    onRestored,
    onRevokedAll,
    pending,
    signOutAction,
    userId,
  ]);

  return { signOut, pending };
}
